// pages/ExpensePurchaseOrder/FindEpoByNumber.js
//
// Legacy menu: "Find EPO by Number" — under EPO.
// Legacy: InputBox prompt → epoDB.getEPO(epoNum) → opens the EPO form.
//
// Access: manager+ (gate prop requireModule="epo.findByNumber" — bundle
// grants this to RoleId 1/2/5 + actual supervisor + IT Admin).
//
// Uses existing API: GET /api/Epo/{epoNumber}. No new backend route.

import React, { useState } from "react";
import { Container, Card, CardBody, Input, InputGroup, Button, Alert, Spinner } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { DashboardPageHeader } from "../../Components/Common/DashboardKit";
import api from "../../api";
import useEpoReviewModal from "../../hooks/useEpoReviewModal";

const FindEpoByNumber = () => {
    const navigate = useNavigate();
    const epoReview = useEpoReviewModal({ permissions: {} });
    void navigate; // retained import; navigation replaced by the review modal
    const [value, setValue] = useState("");
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleFind = async () => {
        setError(null);
        const trimmed = String(value).trim();
        if (!trimmed) { setError("Please enter an EPO number."); return; }
        if (!/^\d+$/.test(trimmed)) { setError("EPO number must be digits only."); return; }

        setLoading(true);
        try {
            const { data } = await api.get(`Epo/${trimmed}`);
            // R28a fix — backend returns { header: {...}, lines: [...] }.
            // Previous code checked data.epoNumber at the top level, which is
            // never present — so even valid EPOs reported "not found". Check
            // header.epoNumber (and the PascalCase fallback) instead.
            const headerData = data?.header ?? data?.Header;
            const found = (headerData?.epoNumber ?? headerData?.EpoNumber)
                       ?? data?.epoNumber ?? data?.EpoNumber;
            if (!found) {
                setError(`No EPO found with number ${trimmed}.`);
                return;
            }
            // Open the full Review EPO modal (replaces the old timeline-only
            // navigation, matching legacy "Find → open the EPO form").
            epoReview.open(trimmed);
        } catch (e) {
            const status = e?.response?.status;
            if (status === 404) setError(`No EPO found with number ${trimmed}.`);
            else if (status === 403) setError("You don't have permission to view that EPO.");
            else setError(e?.message || "Lookup failed.");
        } finally {
            setLoading(false);
        }
    };

    const handleKey = (e) => {
        if (e.key === "Enter") handleFind();
    };

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    title="Find EPO by Number"
                    breadcrumb={[{ label: "EPO", to: "/workflows?wf=epo" }, { label: "Find EPO by Number" }]}
                    subtitle="Look up a specific EPO and open its full review window"
                    breadcrumbItems={[{ label: "EPO", href: "/epo" }, { label: "Find by Number" }]}
                />

                <Card className="mt-3" style={{ maxWidth: 540 }}>
                    <CardBody>
                        <label className="form-label mb-1">EPO Number</label>
                        <InputGroup>
                            <Input
                                type="text"
                                inputMode="numeric"
                                placeholder="e.g. 12345"
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                onKeyDown={handleKey}
                                disabled={loading}
                                autoFocus
                            />
                            <Button color="primary" onClick={handleFind} disabled={loading}>
                                {loading ? <Spinner size="sm" /> : "Find"}
                            </Button>
                        </InputGroup>
                        {error && <Alert color="danger" className="mt-3 mb-0">{error}</Alert>}
                    </CardBody>
                </Card>
            </Container>
        {epoReview.element}
        </div>
    );
};

export default FindEpoByNumber;
